import Heading from '@atlaskit/heading';
import { Stack, Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { useDatabaseContent } from '@databases/database-state';
import {
  type BasicDatabase,
  type BrokenDatabase,
  type EntryCreateOptions,
  type EntryWithContext,
} from '@databases/types/database';
import React, { type FunctionComponent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';

import EntryEmptyState from '../../assets/entry_empty_state.svg';
import searchError from '../../assets/search_error.svg';
import searchIllustration from '../../assets/search_illustration.svg';
import { messages } from '../../i18n';
import { isCreate } from '../entryPicker/DatabaseEntryPicker';

const iconWrapperStyles = xcss({
  padding: 'space.300',
});

const headerStyles = xcss({
  textAlign: 'center',
});

export type DatabasePreviewComponentProps = {
  contentId: string;
  canEditContent: boolean;
  couldEditFields: boolean;
  canEditFields: boolean;
  isContentEditable?: boolean;
  outerContainerRef: HTMLElement | null;
  setOuterContainerRef: (ref: HTMLElement | null) => void;
  hasSelection: boolean;
};

export const DatabasePlaceholder: FunctionComponent<{ error?: boolean }> = ({
  error = false,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Stack alignInline="center" space="space.400">
      <Box xcss={iconWrapperStyles}>
        {/* FIXME: Accessibility issue - jsx-a11y/alt-text */}
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img
          width={108}
          height={108}
          role="presentation"
          src={error ? searchError : searchIllustration}
        />
      </Box>
      {error && (
        <Box xcss={headerStyles}>
          <Heading size="medium">
            {formatMessage(messages['reference-preview.broken-header'])}
          </Heading>
        </Box>
      )}
      <Heading size="small">
        {formatMessage(
          messages[
            error
              ? 'reference-preview.broken-description'
              : 'reference-preview.placeholder'
          ]
        )}
      </Heading>
    </Stack>
  );
};

export const EntryPlaceHolder: FunctionComponent = () => {
  const { formatMessage } = useIntl();

  return (
    <Stack alignInline="center" space="space.150">
      <Box xcss={iconWrapperStyles}>
        <img
          width={242}
          height={111}
          role="presentation"
          src={EntryEmptyState}
          alt={formatMessage(messages['reference-preview.entry-placeholder-header'])}
        />
      </Box>
      <Box xcss={headerStyles}>
        <Heading size="medium">
          {formatMessage(messages['reference-preview.entry-placeholder-header'])}
        </Heading>
      </Box>
      <Text align="center">
        {formatMessage(messages['reference-preview.entry-placeholder-description'])}
      </Text>
    </Stack>
  );
};

const DatabaseComponentContainer = ({
  contentId,
  DatabasePreviewComponent,
}: {
  contentId: string;
  DatabasePreviewComponent: FunctionComponent<DatabasePreviewComponentProps>;
}) => {
  const { setContentId } = useDatabaseContent();

  useEffect(() => {
    setContentId(contentId);
  }, [contentId, setContentId]);

  const [outerContainerRef, setOuterContainerRef] = useState<HTMLElement | null>(null);

  return (
    <DatabasePreviewComponent
      contentId={contentId}
      isContentEditable={false}
      canEditContent={false}
      couldEditFields={false}
      canEditFields={false}
      outerContainerRef={outerContainerRef}
      setOuterContainerRef={setOuterContainerRef}
      hasSelection={false}
    />
  );
};

const placeholderContainerStyles = xcss({
  width: '100%',
  height: '384px',
});

const embedReferenceContainerStyles = xcss({
  maxWidth: '100%',
  overflow: 'hidden',
});

const valueContainerStyles = xcss({
  marginLeft: 'space.200',
  height: '384px',
  marginTop: 'space.0',
  overflow: 'auto',
});

export interface DatabasePreviewProps {
  databaseError: boolean;
  selectedDatabase: BasicDatabase | BrokenDatabase | undefined;
  selectedEntry?: EntryWithContext | EntryCreateOptions | undefined;
  selectedField?: string | undefined;
  DatabasePreviewComponent: FunctionComponent<DatabasePreviewComponentProps>;
  EntryPreviewComponent: FunctionComponent<{
    contentId: string;
    entryId: string;
    isContentEditable?: boolean;
  }>;
  ValuePreviewComponent: FunctionComponent<{
    contentId: string;
    entryId: string;
    fieldId: string;
  }>;
  ToolbarComponent: React.FunctionComponent<{
    contentId: string;
    actions: any;
    canEditFields: boolean;
    canEditContent: boolean;
    showViewSettings?: boolean;
    showName?: boolean;
  }>;
}

export const DatabasePreview: FunctionComponent<DatabasePreviewProps> = ({
  databaseError,
  selectedDatabase,
  DatabasePreviewComponent,
  EntryPreviewComponent,
  ValuePreviewComponent,
  ToolbarComponent,
  selectedEntry,
  selectedField,
}) => {
  if (databaseError) {
    return (
      <Stack alignInline="center" alignBlock="center" xcss={placeholderContainerStyles}>
        <DatabasePlaceholder error={true} />
      </Stack>
    );
  }
  if (selectedField && selectedEntry && 'entry' in selectedEntry && selectedDatabase) {
    return (
      <Box xcss={valueContainerStyles}>
        <ValuePreviewComponent
          contentId={selectedDatabase.contentId}
          entryId={selectedEntry.entry.id}
          fieldId={selectedField}
        />
      </Box>
    );
  }
  if (selectedEntry && isCreate(selectedEntry)) {
    return (
      <Flex xcss={embedReferenceContainerStyles}>
        <Stack alignInline="center" alignBlock="center" xcss={placeholderContainerStyles}>
          <EntryPlaceHolder />
        </Stack>
      </Flex>
    );
  }

  if (selectedEntry && 'entry' in selectedEntry && selectedDatabase) {
    return (
      <Stack xcss={placeholderContainerStyles}>
        <EntryPreviewComponent
          contentId={selectedDatabase.contentId}
          entryId={selectedEntry.entry.id}
          isContentEditable={false}
        />
      </Stack>
    );
  }
  if (selectedDatabase) {
    return (
      <Stack
        xcss={xcss({
          height: '384px',
          overflow: 'hidden',
        })}
      >
        <ToolbarComponent
          contentId={selectedDatabase.contentId}
          canEditContent={false}
          canEditFields={false}
          actions={[]}
          showViewSettings={false}
          showName={false}
        />
        <DatabaseComponentContainer
          contentId={selectedDatabase.contentId}
          DatabasePreviewComponent={DatabasePreviewComponent}
        />
      </Stack>
    );
  }

  return (
    <Flex xcss={embedReferenceContainerStyles}>
      <Stack alignInline="center" alignBlock="center" xcss={placeholderContainerStyles}>
        <DatabasePlaceholder />
      </Stack>
    </Flex>
  );
};
